import { GeneratedCharacter, GeneratedDramaProject, ScriptScene } from './dramaProjectStore';

interface ParseDraftOptions {
  genre: string;
  duration: string;
  keywords: string;
}

const genreLabelMap: Record<string, string> = {
  modern: '现代都市',
  ancient: '古装言情',
  comedy: '喜剧搞笑',
  suspense: '悬疑推理',
  fantasy: '奇幻玄幻',
  youth: '青春校园',
};

const durationLabelMap: Record<string, string> = {
  '1min': '1分钟',
  '3min': '3分钟',
  '5min': '5分钟',
  '10min': '10分钟',
};

const reservedNames = new Set([
  '标题',
  '剧名',
  '片名',
  '一句话卖点',
  '卖点',
  '梗概',
  '简介',
  '人物',
  '角色',
  '主要角色',
  '开场钩子',
]);

const normalizeText = (rawText: string) =>
  rawText
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const toKeywordList = (keywords: string, title: string) => {
  const parts = keywords
    .split(/[、，,\s]+/)
    .map((item) => item.trim())
    .filter(Boolean);

  if (title) {
    parts.unshift(title);
  }

  return Array.from(new Set(parts)).slice(0, 6);
};

const pickTitle = (rawText: string, keywords: string) => {
  const lines = normalizeText(rawText)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  const titleLine = lines.find((line) => /^#/.test(line) || /^(标题|剧名|片名)[：:]/.test(line));
  if (titleLine) {
    return titleLine.replace(/^#+\s*/, '').replace(/^(标题|剧名|片名)[：:]\s*/, '').trim();
  }

  const firstLine = lines[0] || '';
  if (firstLine.length <= 20 && !/^第[一二三123]/.test(firstLine)) {
    return firstLine;
  }

  return keywords.split(/[、，,]/).map((item) => item.trim()).filter(Boolean)[0] || '未命名短剧';
};

const findLabeledValue = (rawText: string, labels: string[]) => {
  const lines = normalizeText(rawText)
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);

  for (const line of lines) {
    for (const label of labels) {
      if (line.startsWith(`${label}：`) || line.startsWith(`${label}:`)) {
        return line.replace(new RegExp(`^${label}[：:]\\s*`), '').trim();
      }
    }
  }

  return '';
};

const detectCharactersFromDialogue = (rawText: string) => {
  const names = new Map<string, number>();
  const matches = rawText.matchAll(/(?:^|\n)\s*([^\s：:\n]{1,8})[：:]/g);

  for (const match of matches) {
    const name = match[1].trim();
    if (
      name.length < 2 ||
      reservedNames.has(name) ||
      /^第[一二三123]/.test(name) ||
      /第[一二三123]集/.test(name)
    ) {
      continue;
    }
    names.set(name, (names.get(name) || 0) + 1);
  }

  return Array.from(names.entries())
    .sort((a, b) => b[1] - a[1])
    .map(([name]) => name)
    .slice(0, 4);
};

const buildCharacters = (rawText: string) => {
  const dialogueNames = detectCharactersFromDialogue(rawText);
  const characters: GeneratedCharacter[] = dialogueNames.map((name, index) => ({
    id: `character-${index + 1}`,
    name,
    age: index === 0 ? '主角年龄待定' : '年龄待定',
    occupation: index === 0 ? '主角职业待补' : '角色职业待补',
    personality: index === 0 ? '需要继续补充人物性格和行为习惯' : '需要继续补充角色性格',
    background: index === 0 ? '根据粘贴初稿整理，建议后续补充前史' : '根据粘贴初稿整理，建议后续补充背景',
    appearance: '外貌待补',
    relationships: [],
  }));

  if (characters.length >= 2) {
    characters[0].relationships.push(`${characters[1].name}：核心关系对象`);
    characters[1].relationships.push(`${characters[0].name}：与主角强绑定`);
  }

  if (characters.length >= 3) {
    characters[0].relationships.push(`${characters[2].name}：推动冲突的人`);
    characters[2].relationships.push(`${characters[0].name}：关键阻力来源`);
  }

  if (characters.length === 0) {
    return [
      {
        id: 'character-1',
        name: '主角',
        age: '年龄待定',
        occupation: '职业待补',
        personality: '根据粘贴初稿暂未识别出明确角色名，建议补角色对白',
        background: '先有剧情，再回填角色前史',
        appearance: '外貌待补',
        relationships: ['关系待补'],
      },
    ];
  }

  return characters;
};

const splitEpisodeBlocks = (rawText: string) => {
  const normalized = normalizeText(rawText);
  const regex = /(第[一二三123]集[^\n]*)/g;
  const matches = Array.from(normalized.matchAll(regex));

  if (matches.length === 0) {
    const paragraphs = normalized.split(/\n\s*\n/).filter(Boolean);
    const chunkSize = Math.max(1, Math.ceil(paragraphs.length / 3));
    return [0, 1, 2].map((index) => ({
      title: `第${index + 1}集`,
      content: paragraphs.slice(index * chunkSize, (index + 1) * chunkSize).join('\n\n').trim(),
    }));
  }

  return matches.map((match, index) => {
    const start = match.index || 0;
    const end = index + 1 < matches.length ? (matches[index + 1].index || normalized.length) : normalized.length;
    const block = normalized.slice(start, end).trim();
    const firstLine = block.split('\n')[0].trim();
    const content = block.slice(firstLine.length).trim();
    return {
      title: firstLine.replace(/[：:]\s*$/, ''),
      content,
    };
  }).slice(0, 3);
};

const firstSentence = (text: string, fallback: string) => {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) {
    return fallback;
  }

  const match = clean.match(/(.+?[。！？!?])/);
  return (match?.[1] || clean).trim();
};

const lastSentence = (text: string, fallback: string) => {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (!clean) {
    return fallback;
  }

  const parts = clean.split(/[。！？!?]/).map((item) => item.trim()).filter(Boolean);
  return parts[parts.length - 1] || fallback;
};

const buildEpisodeBeats = (episodeBlocks: Array<{ title: string; content: string }>) =>
  [0, 1, 2].map((index) => {
    const block = episodeBlocks[index];
    if (!block) {
      return {
        episode: index + 1,
        title: `第${index + 1}集`,
        summary: '待补这一集的关键推进。',
        cliffhanger: '待补这一集的结尾吊点。',
      };
    }

    return {
      episode: index + 1,
      title: block.title,
      summary: firstSentence(block.content, '待补这一集的关键推进。'),
      cliffhanger: lastSentence(block.content, '待补这一集的结尾吊点。'),
    };
  });

const buildScenes = (episodeContent: string, characters: GeneratedCharacter[]) => {
  const rawScenes = episodeContent
    .split(/(?=【[^】]+】)|(?=\[[^\]]+\])|(?=场景\d+[:：])/)
    .map((item) => item.trim())
    .filter(Boolean)
    .slice(0, 4);

  const fallbackCharacters = characters.slice(0, 2).map((character) => character.name);

  const scenes: ScriptScene[] = rawScenes.map((sceneText, index) => {
    const headerMatch = sceneText.match(/^(【([^】]+)】|\[([^\]]+)\]|(场景\d+[:：][^\n]+))/);
    const title = headerMatch?.[2] || headerMatch?.[3] || headerMatch?.[4] || `场景${index + 1}`;
    const body = headerMatch ? sceneText.slice(headerMatch[0].length).trim() : sceneText;
    const dialogueMatches = Array.from(body.matchAll(/([^\s：:\n]{2,8})[：:]\s*([^\n]+)/g));
    const sceneCharacters = Array.from(new Set(dialogueMatches.map((match) => match[1].trim()).filter(Boolean)));

    return {
      id: `scene-${index + 1}`,
      title,
      description: firstSentence(body, '待补镜头描述'),
      duration: index === 0 ? '0:30' : '0:45',
      location: title,
      characters: sceneCharacters.length > 0 ? sceneCharacters : fallbackCharacters,
      shotType: index === 0 ? '中景' : '近景',
      cameraMovement: index === 0 ? '推镜头' : '固定',
      lighting: '按原稿氛围补灯光',
      sound: '环境声',
      dialogue: dialogueMatches[0]
        ? `${dialogueMatches[0][1]}：${dialogueMatches[0][2].trim()}`
        : firstSentence(body, '待补关键对白'),
      notes: '由粘贴初稿自动整理，建议继续精修镜头节奏。',
    };
  });

  if (scenes.length > 0) {
    return scenes;
  }

  return [
    {
      id: 'scene-1',
      title: '场景1',
      description: firstSentence(episodeContent, '待补镜头描述'),
      duration: '0:30',
      location: '待定场景',
      characters: fallbackCharacters,
      shotType: '中景',
      cameraMovement: '固定',
      lighting: '按原稿氛围补灯光',
      sound: '环境声',
      dialogue: firstSentence(episodeContent, '待补关键对白'),
      notes: '由粘贴初稿自动整理，建议继续精修镜头节奏。',
    },
  ];
};

export const buildFreeToolPrompt = (options: ParseDraftOptions) => [
  '请帮我写一个可拍的中文短剧初稿，不要空泛，不要课程示例口吻。',
  `题材：${genreLabelMap[options.genre] || '短剧'}`,
  `时长：${durationLabelMap[options.duration] || '3分钟'}`,
  `关键词：${options.keywords || '请自行补充关键词'}`,
  '请按下面结构输出：',
  '1. 标题',
  '2. 一句话卖点',
  '3. 开场钩子',
  '4. 主要角色（至少2个）',
  '5. 三集推进（每集一句话+结尾吊点）',
  '6. 第1集完整正文（包含场景、动作、对白）',
  '7. 可拍分镜草稿（至少2个镜头）',
].join('\n');

export const parsePastedDramaDraft = (
  rawDraft: string,
  options: ParseDraftOptions,
): GeneratedDramaProject => {
  const normalized = normalizeText(rawDraft);

  if (normalized.length < 80) {
    throw new Error('粘贴内容太短了，至少贴一段完整剧情或一集正文再整理。');
  }

  const title = pickTitle(normalized, options.keywords);
  const episodeBlocks = splitEpisodeBlocks(normalized);
  const episodeBeats = buildEpisodeBeats(episodeBlocks);
  const fullScriptText = episodeBlocks[0]?.content || normalized;
  const characters = buildCharacters(normalized);
  const scriptScenes = buildScenes(fullScriptText, characters);
  const labeledLogline = findLabeledValue(normalized, ['一句话卖点', '梗概', '简介']);
  const labeledHook = findLabeledValue(normalized, ['开场钩子']);

  return {
    title,
    genreLabel: genreLabelMap[options.genre] || '短剧',
    durationLabel: durationLabelMap[options.duration] || '3分钟',
    keywords: toKeywordList(options.keywords, title),
    creativity: 5,
    logline: labeledLogline || firstSentence(fullScriptText, '待补一句话卖点'),
    hook: labeledHook || firstSentence(fullScriptText, '待补开场钩子'),
    tone: '根据免费版初稿整理，建议继续微调风格和情绪强度',
    characters,
    episodeBeats,
    scriptScenes,
    nextEpisodeHook: episodeBeats[1]?.cliffhanger || lastSentence(fullScriptText, '待补下一集吊点'),
    fullScriptText,
    generatedAt: new Date().toISOString(),
  };
};
